import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Form, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import WorkspaceDataTable from '../../components/workspace/WorkspaceDataTable';
import WorkspacePageHeader from '../../components/workspace/WorkspacePageHeader';
import { useAdminRecordDelete } from '../../components/workspace/useAdminRecordDelete';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { formatWorkspaceDate } from '../../utils/workspaceDates';
import { platformAPI } from '../../services/platformAPI';
import './tender/TimesheetsPage.scss';

const STATUS_LABELS = {
  PENDING: 'Pending',
  IN_PROGRESS: 'In progress',
  OVERDUE: 'Overdue',
  COMPLETED: 'Approved',
  CANCELLED: 'Returned'
};

const STATUS_TONE = {
  PENDING: 'submitted',
  IN_PROGRESS: 'submitted',
  OVERDUE: 'returned',
  COMPLETED: 'approved',
  CANCELLED: 'returned'
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return formatWorkspaceDate(date);
};

const ApprovalsPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [includeDecided, setIncludeDecided] = useState(false);

  useDocumentTitle('Approvals');

  const load = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const response = await platformAPI.listApprovalQueue(includeDecided ? { includeDecided: 'true' } : {});
      setRows(response.data?.data?.items || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load approvals');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [includeDecided]);

  useEffect(() => {
    load();
  }, [load]);

  const admin = useAdminRecordDelete({
    entity: 'workItem',
    noun: 'approval',
    plural: 'approvals',
    reload: load,
    label: (row) => row.title,
    note: 'The approval task is removed. The tender, estimate, or timesheet it points at stays.'
  });

  const tableRows = useMemo(
    () => rows.map((row) => ({
      ...row,
      typeLabel: row.typeLabel || row.type,
      tenderLabel: row.tenderReference
        ? `${row.tenderReference}${row.tenderTitle ? ` — ${row.tenderTitle}` : ''}`
        : '—',
      raisedLabel: row.raisedByName || '—',
      waitingLabel: formatDate(row.createdAt),
      dueLabel: formatDate(row.dueDate),
      statusLabel: STATUS_LABELS[row.status] || row.status
    })),
    [rows]
  );

  const columns = useMemo(
    () => [
      { key: 'title', label: 'Request', mobileTitle: true },
      { key: 'typeLabel', label: 'Type', width: '170px' },
      { key: 'tenderLabel', label: 'Tender' },
      { key: 'raisedLabel', label: 'Raised by', width: '160px' },
      { key: 'dueLabel', label: 'Due', width: '130px' },
      {
        key: 'statusLabel',
        label: 'Status',
        width: '130px',
        render: (value, row) => (
          <span className={`ts-page__badge ts-page__badge--${STATUS_TONE[row.status] || 'draft'}`}>{value}</span>
        )
      }
    ],
    []
  );

  const openRecord = (row, mode) => {
    const id = row._id || row.id;
    if (!id) return;
    navigate(mode === 'decide' ? `/apps/tender/approvals/${id}/decide` : `/apps/tender/approvals/${id}`);
  };

  if (loading) return <Spinner animation="border" size="sm" />;

  return (
    <div className="ts-page">
      <WorkspacePageHeader
        title="Approvals"
        help="approvals"
        subtitle="Requests waiting for a decision. Open one to read it, then approve it or send it back."
        actions={(
          <Form.Check
            type="switch"
            id="approvals-include-decided"
            label="Include decided"
            checked={includeDecided}
            onChange={(event) => setIncludeDecided(event.target.checked)}
          />
        )}
      />
      {error ? <Alert variant="danger">{error}</Alert> : null}
      {admin.notice}
      {!error && tableRows.length === 0 ? (
        <Alert variant="info">Nothing is waiting for a decision.</Alert>
      ) : null}
      <WorkspaceDataTable
        title="Approval requests"
        data={tableRows}
        columns={columns}
        searchPlaceholder="Search request, type, tender…"
        pageSize={12}
        onView={(row) => openRecord(row, 'view')}
        onEdit={(row) => openRecord(row, 'decide')}
        {...admin.tableProps}
        rowActions={admin.wrapActions((row) => (
          row.canDecide
            ? [{ type: 'view', label: 'View' }, { type: 'edit', label: 'Decide' }]
            : [{ type: 'view', label: 'View' }]
        ))}
      />
      {admin.modal}
    </div>
  );
};

export default ApprovalsPage;
